/**
 * Servers list page. Servers are owned by an organisation and visible via the
 * org-tree cascade — so this looks structurally like the Applications page,
 * grouped by owning org, with a card per server.
 */
import {
  HardDrive,
  Plus,
  Loader2,
  Building2,
  ChevronRight,
  Globe,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { CreateServerDialog } from '@/components/applications/CreateServerDialog.js';
import { api, type Server } from '@/api/client.js';
import { useAuth } from '@/lib/auth.js';

export function ServersPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { memberships } = useAuth();

  const query = useQuery({ queryKey: ['servers'], queryFn: () => api.servers.listAll() });

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold tracking-tight">Servers</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Named hosts your components run on. Each server lives under one organisation;
            access via SSH / docker / kubectl is configured per component.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={memberships.length === 0}>
          <Plus className="h-4 w-4" />
          New server
        </Button>
      </header>

      {query.isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Could not load servers.
          </CardContent>
        </Card>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState onCreate={() => setOpen(true)} />
      ) : (
        <ServersByOrg servers={query.data!} />
      )}

      <CreateServerDialog
        open={open}
        onOpenChange={setOpen}
        memberships={memberships}
        onCreated={async () => {
          await queryClient.invalidateQueries({ queryKey: ['servers'] });
        }}
      />
    </div>
  );
}

function ServersByOrg({ servers }: { servers: Server[] }): JSX.Element {
  const byOrg = new Map<string, { orgName: string; orgSlug: string; servers: Server[] }>();
  for (const s of servers) {
    if (!byOrg.has(s.orgId)) {
      byOrg.set(s.orgId, { orgName: s.orgName, orgSlug: s.orgSlug, servers: [] });
    }
    byOrg.get(s.orgId)!.servers.push(s);
  }
  return (
    <div className="space-y-6">
      {[...byOrg.entries()].map(([orgId, group]) => (
        <section key={orgId} className="space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" />
            <Link to="/orgs/$slug" params={{ slug: group.orgSlug }} className="hover:text-foreground">
              {group.orgName}
            </Link>
            <span className="font-mono normal-case">/{group.orgSlug}</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {group.servers.map((s) => (
              <ServerCard key={s.id} server={s} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ServerCard({ server }: { server: Server }): JSX.Element {
  return (
    <Link
      to="/p/applications/servers/$slug"
      params={{ slug: server.slug }}
      search={{ org: server.orgSlug }}
      className="block"
    >
      <Card className="h-full transition-colors hover:border-primary/60">
        <CardHeader className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base">{server.name}</CardTitle>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <CardDescription className="text-xs">
            <span className="font-mono">/{server.slug}</span>
            {server.hostname && (
              <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
                <Globe className="h-3 w-3" />
                <span className="font-mono">{server.hostname}</span>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        {server.description && (
          <CardContent className="pt-0">
            <p className="line-clamp-2 text-sm text-muted-foreground">{server.description}</p>
          </CardContent>
        )}
      </Card>
    </Link>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <HardDrive className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-medium">No servers yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Add the hosts your components will run on. Access methods come later — this just
          captures the identity of each box.
        </p>
        <Button onClick={onCreate} className="mt-4">
          <Plus className="h-4 w-4" />
          New server
        </Button>
      </CardContent>
    </Card>
  );
}
